/**
 * Sanitize text for PDF generation by stripping emoji and normalizing
 * problematic Unicode characters that jsPDF's default fonts cannot render
 * or that cause splitTextToSize to miscalculate line breaks.
 *
 * This is the single chokepoint for all text heading into jsPDF, including
 * the HTML pipeline (addHtmlContent / measureHtmlToFlatLines).
 */
export const sanitizeTextForPDF = (text: string): string => {
  if (!text) return text;

  return (
    text
      // Strip emoji — jsPDF default fonts don't support Unicode emoji
      .replace(
        /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{231A}-\u{231B}]|[\u{23E9}-\u{23F3}]|[\u{23F8}-\u{23FA}]|[\u{25AA}-\u{25AB}]|[\u{25B6}]|[\u{25C0}]|[\u{25FB}-\u{25FE}]|[\u{2614}-\u{2615}]|[\u{2648}-\u{2653}]|[\u{267F}]|[\u{2693}]|[\u{26A1}]|[\u{26AA}-\u{26AB}]|[\u{26BD}-\u{26BE}]|[\u{26C4}-\u{26C5}]|[\u{26CE}]|[\u{26D4}]|[\u{26EA}]|[\u{26F2}-\u{26F3}]|[\u{26F5}]|[\u{26FA}]|[\u{26FD}]|[\u{2702}]|[\u{2705}]|[\u{2708}-\u{270D}]|[\u{270F}]|[\u{2712}]|[\u{2714}]|[\u{2716}]|[\u{271D}]|[\u{2721}]|[\u{2728}]|[\u{2733}-\u{2734}]|[\u{2744}]|[\u{2747}]|[\u{274C}]|[\u{274E}]|[\u{2753}-\u{2755}]|[\u{2757}]|[\u{2763}-\u{2764}]|[\u{2795}-\u{2797}]|[\u{27A1}]|[\u{27B0}]|[\u{27BF}]|[\u{2934}-\u{2935}]|[\u{2B05}-\u{2B07}]|[\u{2B1B}-\u{2B1C}]|[\u{2B50}]|[\u{2B55}]|[\u{3030}]|[\u{303D}]|[\u{3297}]|[\u{3299}]|[\u{FE00}-\u{FE0F}]|[\u{200D}]/gu,
        ''
      )
      // Replace various types of dashes with regular ASCII hyphen
      .replace(/[\u2010-\u2015\u2212\uFE58\uFE63\uFF0D]/g, '-')
      // Replace various types of quotes with regular ASCII quotes
      .replace(/[\u2018\u2019\u201A\u201B]/g, "'")
      .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
      // Replace various types of spaces with regular space
      .replace(/[\u00A0\u1680\u2000-\u200B\u202F\u205F\u3000\uFEFF]/g, ' ')
      // Replace various bullet points with regular asterisk or dash
      .replace(/[\u2022\u2023\u2043\u204C\u204D\u2219\u25D8\u25E6\u2981]/g, '•')
      // Normalize line breaks
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      // Remove zero-width characters that can cause issues
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      // Ensure proper spacing after punctuation
      .replace(/\.([A-Z])/g, '. $1')
      .replace(/!([A-Z])/g, '! $1')
      .replace(/\?([A-Z])/g, '? $1')
      .replace(/:([A-Z])/g, ': $1')
      // Handle punctuation before numbers (avoid breaking decimals)
      .replace(/(?<!\d)\.(\d)/g, '. $1')
      .replace(/!(\d)/g, '! $1')
      .replace(/\?(\d)/g, '? $1')
      // Handle closing parenthesis before capital letter or number
      .replace(/\)([A-Z])/g, ') $1')
      .replace(/\)(\d)/g, ') $1')
      // Normalize multiple consecutive spaces (do this last)
      .replace(/ {2,}/g, ' ')
  );
};
